import React, { useMemo } from 'react'
import { Tooltip, Icon, Typography, Box } from '@mui/material'
import { withStyles } from '@mui/styles'
import { Rating } from '@mui/material'
import { ratingInputStyles } from './rating-input.styles'

/**
 * @typedef {object} RatingInputComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, el rating se muestra como solo lectura.
 * @property {boolean} [explicit=false] - Si es `true` y `readOnly` es `true`, muestra el valor numérico junto a las estrellas.
 * @property {'small' | 'medium' | 'large'} [size='medium'] - El tamaño de las estrellas.
 * @property {string} [title] - El título que se muestra sobre el componente de rating.
 * @property {string} [tooltip] - Un texto de ayuda que se muestra en un tooltip junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {number} [value] - El valor actual del rating (número de estrellas).
 * @property {string} name - El nombre del campo.
 * @property {function(object): void} onChange - La función callback que se ejecuta cuando el rating cambia. Recibe un objeto con `target.name` y `target.value`.
 * @property {number} [precision] - La precisión del rating (ej. 0.5 para medias estrellas, 1 para estrellas completas).
 */

/**
 * Un componente de entrada de calificación por estrellas usando Material-UI Rating.
 * 
 * Permite a los usuarios proporcionar una calificación mediante estrellas. Soporta modo de solo lectura
 * con opción de mostrar el valor numérico explícitamente. El tamaño de las estrellas se ajusta
 * automáticamente en modo edición (siempre 'large') y respeta la prop `size` en modo lectura.
 * 
 * @component
 * @example
 * const MyReviewForm = () => {
 *   const [rating, setRating] = useState(3);
 * 
 *   const handleChange = (event) => {
 *     setRating(event.target.value);
 *   };
 * 
 *   return (
 *     <RatingInput
 *       name="productRating"
 *       title="Calificación del Producto"
 *       tooltip="Seleccione de 1 a 5 estrellas"
 *       value={rating}
 *       onChange={handleChange}
 *       precision={0.5}
 *     />
 *   );
 * };
 * 
 * @example
 * // Modo solo lectura con valor explícito
 * <RatingInput
 *   title="Calificación promedio"
 *   value={4.5}
 *   readOnly
 *   explicit
 *   size="small"
 * />
 * 
 * @param {RatingInputComponentProps} props - Las props del componente RatingInput.
 */
const RatingInputComponent = ({
  classes,
  readOnly = false,
  explicit = false,
  size = 'medium',
  ...props
}) => {
  const value = props.value ? props.value : null

  const tooltip = useMemo(() => {
    return props.tooltip ? (
      <span style={{ fontSize: 16, marginLeft: 4 }}>
        <Tooltip
          placement="top"
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {props.tooltip}
            </Typography>
          }
        >
          <Icon fontSize="inherit">help_outline</Icon>
        </Tooltip>
      </span>
    ) : null
  }, [props.tooltip])

  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (_, value) => {
    props.onChange({ target: { name: props.name, value } })
  }

  return (
    <>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title} {tooltip}
        </Typography>
      ) : null}
      <Box
        display="flex"
        alignItems="center"
        className={readOnly ? classes.readMode : classes.editMode}
      >
        <Rating
          {...{
            name: props.name,
            value: value,
            readOnly,
            size: readOnly ? size : 'large',
            onChange: handleChange,
            precision: props.precision,
          }}
        />
        {readOnly && explicit ? (
          <Box ml={1}>
            <Typography size={readOnly ? size : 'large'} variant={`body${typographyVariant}`}>
              {value ? value : ''}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </>
  )
}

export const RatingInput = React.memo(withStyles(ratingInputStyles)(RatingInputComponent))
